import classNames from "classnames";
import moment from "moment";
import React from "react";
import DateTime from "react-datetime";
import * as DateUtil from "@travellocal/utils/dateHelpers";

import "./dateTimePicker.scss";

export enum PickerMode {
  DateTime = 0,
  Date,
}

interface Props {
  inputDisabled?: boolean;
  value?: IDateTime|IDate;
  placeholder?: string;
  onChange: (dt: IDateTime) => void;
  startDateTime?: IDateTime|IDate;
  endDateTime?: IDateTime|IDate;
  mode?: PickerMode;
  pickInUtc?: boolean;
}

interface State {
  isValid: boolean;
}

/**
 * Adapter component for react-datetime to bothend datetime. Only supports UTC
 */
export class DateTimePicker extends React.Component<Props, State> {
  public constructor(props) {
    super(props);
    this.state = {
      isValid: null,
    };
  }

  private notifyChange = (m: moment.Moment): void => {
    let bothendTime = null;
    if (m != null) {
      // Tidy values that can't be changed in the UI
      m.seconds(0);
      m.milliseconds(0);

      if (this.props.mode === PickerMode.Date) {
        m.minutes(0);
        m.hours(0);
      }

      bothendTime = DateUtil.momentToDateTime(m);
    }

    this.props.onChange(bothendTime);
  }

  private handleInputChange = (input: string|moment.Moment): void => {
    let isValid = true;

    // Input is sourced from either the picker, or the text input box.
    if (typeof input === "string") {
      const hasInput = input != null && input.trim().length > 0;
      if (hasInput) {
        const m = moment(input, "DD/MM/YYYY HH:mm", true);
        if (m.isValid()) { // If it's not valid, let's assume the user is in the process of making it valid and do nothing.
          this.notifyChange(m);
        }

        isValid = false;
      }
      else {
        this.notifyChange(null); // Allow unsetting via text input
      }
    }
    else {
      // Assuming values from the picker are valid...
      this.notifyChange(input);
    }

    this.setState({ isValid });
  }

  private canSelectDate = (date: moment.Moment): boolean => {
    const startMoment = this.props.startDateTime ? DateUtil.noTimeZoneAndDST(DateUtil.toMoment(this.props.startDateTime, true)) : null;
    const endMoment = this.props.endDateTime ? DateUtil.noTimeZoneAndDST(DateUtil.toMoment(this.props.endDateTime, true)) : null;

    if (startMoment && endMoment) {
      return date.isBetween(startMoment, endMoment);
    }
    else if (startMoment) {
      return date.isAfter(startMoment);
    }
    else if (endMoment) {
      return date.isBefore(endMoment);
    }
    else {
      return true;
    }
  }

  public render() {
    const pickInUtc = this.props.pickInUtc !== null ? this.props.pickInUtc : true;
    const placeholderText = this.props.placeholder || "No time chosen";

    let momentDate = this.props.value != null ? DateUtil.toMoment(this.props.value, pickInUtc) : "";
    const hasTimePicker = this.props.mode !== PickerMode.Date;
    if (!pickInUtc && typeof momentDate !== "string") {
      momentDate = momentDate.local() ; // picker component doesn't convert if passing in a date.
    }

    return (
      <DateTime
        className="date-time-picker"
        utc={pickInUtc}
        dateFormat="DD MMM YYYY" // fix to GB dates, to be consistent with the rest of the app
        // open={this.props.open}
        timeFormat={hasTimePicker}
        inputProps={{
          disabled: this.props.inputDisabled,
          placeholder: placeholderText,
          className: classNames({invalid: this.state.isValid === false }),
        }}
        defaultValue={momentDate as any} // typings are broke
        value={momentDate as any}
        isValidDate={this.canSelectDate}
        onChange={input => this.handleInputChange(input)}
      />
    );
  }
}
